/**
 * ticketItemBlocks.ts — Agrupación y helpers de presentación para filas de ticket.
 *
 * QUÉ EXPORTA: groupTicketItemBlocks, helpers de modo de grupo y etiquetas.
 * QUÉ NO DEBE HACER: Calcular precios ni descuentos.
 */

import { TICKET_BASE_PRICE_LABEL } from "./ticketLineBreakdown";

export interface TicketBlockRow {
  nombre: string;
  cantidad?: number;
  peso?: number;
  precioUnitario?: number;
  lineSubtotal?: number;
  /** Precio de lista original (antes de precio mayorista/VIP/descuentos). */
  precioUnitarioOriginal?: number;
  isGroupHeader?: boolean;
  isSubLine?: boolean;
  isGroupFooter?: boolean;
  isDiscountLine?: boolean;
  isArticleTotalLine?: boolean;
  isTaxLine?: boolean;
  taxRatePct?: number;
}

/** Agrupa filas en bloques de producto (simple o expandido con sub-líneas). */
export function groupTicketItemBlocks<T extends TicketBlockRow>(
  items: T[],
): T[][] {
  const blocks: T[][] = [];
  let i = 0;
  while (i < items.length) {
    const item = items[i];
    if (item.isGroupHeader) {
      const block: T[] = [item];
      i += 1;
      while (
        i < items.length &&
        (items[i].isSubLine ||
          items[i].isGroupFooter ||
          items[i].isDiscountLine ||
          items[i].isArticleTotalLine ||
          items[i].isTaxLine)
      ) {
        block.push(items[i]);
        i += 1;
      }
      blocks.push(block);
    } else if (item.isDiscountLine || item.isArticleTotalLine) {
      i += 1;
    } else {
      const block: T[] = [item];
      i += 1;
      while (
        i < items.length &&
        (items[i].isSubLine ||
          items[i].isTaxLine ||
          items[i].isDiscountLine ||
          items[i].isArticleTotalLine)
      ) {
        block.push(items[i]);
        i += 1;
      }
      blocks.push(block);
    }
  }
  return blocks;
}

export function blockHeaderTotal(header: TicketBlockRow): number {
  return Math.round(
    header.lineSubtotal ??
      (header.precioUnitario ?? 0) * (header.cantidad ?? 1),
  );
}

export const TICKET_UNIT_PRICE_LABEL = "P.UNIT";

/**
 * Precio por unidad que se muestra en el ítem, en base SIN IVA cuando la
 * tarifa es > 0 (para que todo el desglose use la misma base: sin IVA + IVA
 * (incluido) = total). Cuando la línea tuvo descuento se parte del precio de
 * lista (referencia "antes"); si no, del unitario real (total ÷ cantidad).
 */
export function resolveTicketBlockUnitPrice(
  header: TicketBlockRow,
): number | null {
  if (header.peso != null && header.peso > 0) return null;
  const qty = Math.max(1, header.cantidad || 1);
  const total = blockHeaderTotal(header);
  if (total <= 0) return null;
  const realUnit = Math.round(total / qty);
  const listUnit =
    header.precioUnitarioOriginal != null && header.precioUnitarioOriginal > 0
      ? Math.round(header.precioUnitarioOriginal)
      : null;
  const unitWithTax =
    listUnit != null && listUnit > realUnit ? listUnit : realUnit;
  const taxRatePct = header.taxRatePct ?? 0;
  if (taxRatePct <= 0) return unitWithTax;
  return Math.round(unitWithTax / (1 + taxRatePct / 100));
}

/** Total de una sub-línea de ticket (precio × cantidad de la línea). */
export function ticketSubLineLineTotal(row: TicketBlockRow): number {
  if (row.lineSubtotal != null) return Math.round(row.lineSubtotal);
  const qty = Math.max(1, row.cantidad ?? 1);
  return Math.round((row.precioUnitario ?? 0) * qty);
}

export function formatTicketSublineAmount(
  total: number,
  money: (n: number) => string,
): string {
  const abs = Math.abs(Math.round(total));
  return total < 0 ? `-${money(abs)}` : money(abs);
}

/** Combo menú con Precio Base (ej. Almuerzo). */
export function isMenuGroupBlock(block: TicketBlockRow[]): boolean {
  return block.some(
    (it) => it.isSubLine && it.nombre === TICKET_BASE_PRICE_LABEL,
  );
}

/** Hub de adiciones sin precio base propio (ej. Adiciones al Pedido). */
export function isAddonsGroupBlock(block: TicketBlockRow[]): boolean {
  const header = block[0];
  return !!header?.isGroupHeader && !isMenuGroupBlock(block);
}

/** Quita prefijos decorativos (+ / −) de sub-líneas de ticket. */
export function stripTicketSubLineName(nombre: string): string {
  return nombre.replace(/^[+−]\s+/, "").trim();
}

/** Partes de resumen de variantes inventario (`Grupo: Opc · …`) en líneas separadas. */
export function splitTicketVariantSummary(text: string): string[] {
  return text
    .split(" · ")
    .map((part) => part.trim())
    .filter(Boolean);
}
